import React, { useMemo } from 'react';
import { FormField, Input, Select } from '@/shared/components/ui';
import { usePipelinesDropdown } from '@/modules/helpdesk/pipelines/hooks';
import { useUsersDropdown } from '@/modules/users/hooks/useUsers';
import { useCustomersDropdown } from '@/modules/crm/customers/hooks';

export interface ProjectIdentityFieldsProps {
  name: string;
  onNameChange: (v: string) => void;
  /** Vínculo com `customers` (ref). String vazia = "— Sem cliente —". */
  clientId: string;
  onClientIdChange: (v: string) => void;
  color: string;
  onColorChange: (v: string) => void;
  pipelineId: string;
  onPipelineChange: (v: string) => void;
  leaderId: string;
  onLeaderChange: (v: string) => void;
  /** Prefixo dos data-testid (default `project-identity`). */
  testIdPrefix?: string;
}

/**
 * Campos de identidade do projeto (nome, cliente, pipeline, líder, cor).
 * Presentacional, reutilizado no formulário de criação (Task 12) e na aba de
 * configurações do workspace (Task 13). Pipeline, usuários e clientes (Plano
 * C, Task 6) via rotas dropdown.
 */
export const ProjectIdentityFields: React.FC<ProjectIdentityFieldsProps> = ({
  name,
  onNameChange,
  clientId,
  onClientIdChange,
  color,
  onColorChange,
  pipelineId,
  onPipelineChange,
  leaderId,
  onLeaderChange,
  testIdPrefix = 'project-identity',
}) => {
  const { data: pipelines, isLoading: loadingPipelines } = usePipelinesDropdown();
  const { data: users, isLoading: loadingUsers } = useUsersDropdown();
  const { data: customers, isLoading: loadingCustomers } = useCustomersDropdown();

  const pipelineOptions = useMemo(
    () => (pipelines ?? []).map((p) => ({ value: p.id, label: p.name })),
    [pipelines],
  );

  const leaderOptions = useMemo(
    () => (users ?? []).map((u) => ({ value: u.id, label: u.name })),
    [users],
  );

  const clientOptions = useMemo(
    () => [
      { value: '', label: loadingCustomers ? 'Carregando...' : '— Sem cliente —' },
      ...(customers ?? []).map((c) => ({ value: c.id, label: c.name })),
    ],
    [customers, loadingCustomers],
  );

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <FormField label="Nome" required className="md:col-span-2">
        <Input
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          placeholder="Ex: Implantação ERP"
          data-testid={`${testIdPrefix}-name`}
        />
      </FormField>

      <Select
        label="Cliente"
        options={clientOptions}
        value={clientId}
        onChange={onClientIdChange}
        data-testid={`${testIdPrefix}-client`}
      />

      <Select
        label="Pipeline dos tickets"
        required
        options={pipelineOptions}
        value={pipelineId}
        onChange={onPipelineChange}
        placeholder={loadingPipelines ? 'Carregando...' : 'Selecione o pipeline'}
        data-testid={`${testIdPrefix}-pipeline`}
      />

      <Select
        label="Líder do projeto"
        required
        options={leaderOptions}
        value={leaderId}
        onChange={onLeaderChange}
        placeholder={loadingUsers ? 'Carregando...' : 'Selecione o líder'}
        data-testid={`${testIdPrefix}-leader`}
      />

      <FormField label="Cor">
        <div className="flex items-center gap-3 max-w-xs">
          <input
            type="color"
            value={color}
            onChange={(e) => onColorChange(e.target.value)}
            className="w-12 h-9 rounded cursor-pointer border"
            style={{ borderColor: 'var(--theme-input-border)' }}
            data-testid={`${testIdPrefix}-color-picker`}
          />
          <Input
            value={color}
            onChange={(e) => onColorChange(e.target.value)}
            className="font-mono"
            placeholder="#8b5cf6"
            data-testid={`${testIdPrefix}-color-hex`}
          />
        </div>
      </FormField>
    </div>
  );
};

ProjectIdentityFields.displayName = 'ProjectIdentityFields';
